<template>
	<section
		v-if="shouldShow"
		class="limited-coupon"
		aria-labelledby="limited-coupon-title"
		@mouseenter="handleCarouselMouseEnter"
		@mouseleave="handleCarouselMouseLeave"
		@focusin="handleCarouselFocusIn"
		@focusout="handleCarouselFocusOut"
	>
		<header class="limited-coupon__header">
			<h2 id="limited-coupon-title">{{ $t('super_coupon_title') }}</h2>
			<p v-if="formattedEndTime">
				<span>{{ $t('super_coupon_end_time') }}</span>
				<time>{{ formattedEndTime }}</time>
			</p>
		</header>

		<div
			class="limited-coupon__slider"
			@touchstart.passive="handleTouchStart"
			@touchend.passive="handleTouchEnd"
			@touchcancel.passive="handleTouchCancel"
		>
			<button
				v-if="hasMultipleActivities"
				class="limited-coupon__nav limited-coupon__nav--previous"
				type="button"
				:aria-label="$t('super_coupon_previous_activity')"
				@click="showPreviousActivity"
			>
				<i class="fa fa-chevron-left" aria-hidden="true"></i>
			</button>

			<transition :name="carouselTransitionName" mode="out-in">
			<article :key="activityKey" class="limited-coupon__product"
				:class="{ 'limited-coupon__product--no-value': !realValueText }">
				<span class="limited-coupon__badge">{{ $t('super_coupon_recommended') }}</span>
				<button ref="detailsTrigger" class="limited-coupon__info" type="button"
					:aria-label="$t('super_coupon_view_details')" @click="openDetails">
					<i class="fa fa-info" aria-hidden="true"></i>
				</button>

				<div class="limited-coupon__top">
					<div class="limited-coupon__summary">
						<div class="limited-coupon__image-wrap">
							<img v-if="activity.icon" :src="activity.icon" :alt="packageTitle">
						</div>
						<div class="limited-coupon__copy">
							<h3>{{ packageTitle }}</h3>
							<p>{{ promotionalCopyText }}</p>
							<strong>{{ currentPrice }}</strong>
						</div>
					</div>
					<div v-if="realValueText" class="limited-coupon__value">
						<div>
							<span>{{ $t('super_coupon_value_label') }}</span>
							<strong>{{ realValueText }}</strong>
						</div>
					</div>
				</div>

				<div class="limited-coupon__bottom">
					<div class="limited-coupon__benefit-copy">
						<ul class="limited-coupon__benefits" :aria-label="$t('super_coupon_contents')">
							<li v-for="(benefit, index) in benefits" :key="`${benefit.threshold}-${index}`"
								:class="`limited-coupon__benefit--${benefit.state}`">
								<span class="limited-coupon__threshold">{{ $t('super_coupon_threshold', { amount: benefit.threshold }) }}</span>
								<strong class="limited-coupon__discount">{{ $t('super_coupon_discount', { amount: benefit.discount }) }}</strong>
								<small v-if="benefit.state === 'active'">{{ $t('super_coupon_remaining', { time: benefit.remainingTime }) }}</small>
								<small v-else-if="benefit.statusText">{{ benefit.statusText }}</small>
								<small v-else>{{ benefit.validity }}</small>
								<span v-if="benefit.statusText" class="limited-coupon__status-stamp">
									{{ benefit.statusText }}
								</span>
							</li>
						</ul>
						<p v-if="purchaseRuleText" class="limited-coupon__purchase-rule">{{ purchaseRuleText }}</p>
					</div>
					<button id="superCouponBuyButton" class="limited-coupon__buy" type="button"
						:class="{ 'limited-coupon__buy--purchased': hasPurchased }"
						:disabled="paymentOpening || hasPurchased"
						@click="openPayment">
						{{ $t(hasPurchased ? 'super_coupon_purchased' : 'super_coupon_buy_now') }}
					</button>
				</div>
				</article>
			</transition>

			<button
				v-if="hasMultipleActivities"
				class="limited-coupon__nav limited-coupon__nav--next"
				type="button"
				:aria-label="$t('super_coupon_next_activity')"
				@click="showNextActivity"
			>
				<i class="fa fa-chevron-right" aria-hidden="true"></i>
			</button>
		</div>

		<div v-if="hasMultipleActivities" class="limited-coupon__pagination" :aria-label="$t('super_coupon_switcher')">
			<button
				v-for="(item, index) in activities"
				:key="`activity-dot-${item.activityId || index}`"
				class="limited-coupon__dot"
				:class="{ 'limited-coupon__dot--active': index === currentActivityIndex }"
				type="button"
				:aria-label="$t('super_coupon_switch_to', { index: index + 1 })"
				:aria-current="index === currentActivityIndex ? 'true' : null"
				@click="selectActivity(index)"
			></button>
		</div>

		<div v-if="showDetails" class="super-coupon-dialog__mask" @click.self="closeDetails">
			<div ref="detailsPanel" class="super-coupon-dialog" role="dialog" aria-modal="true"
				aria-labelledby="super-coupon-dialog-title">
				<div class="super-coupon-dialog__header">
					<h3 id="super-coupon-dialog-title">{{ $t('super_coupon_details_title') }}</h3>
					<button ref="detailsClose" class="super-coupon-dialog__close" type="button"
						:aria-label="$t('super_coupon_close_details')" @click="closeDetails">
						<i class="fa fa-close" aria-hidden="true"></i>
					</button>
				</div>

				<ul v-if="detailCoupons.length" class="super-coupon-dialog__list">
					<li v-for="coupon in detailCoupons" :key="coupon.id"
						:class="`super-coupon-dialog__item--${coupon.state}`">
						<span>{{ coupon.threshold }}</span>
						<strong>{{ coupon.discount }}</strong>
						<small v-if="coupon.stateText">{{ coupon.stateText }}</small>
					</li>
				</ul>
				<p v-else class="super-coupon-dialog__empty">{{ $t('super_coupon_empty') }}</p>

				<div v-if="usageInstructionsText" class="super-coupon-dialog__instructions">
					<h4>{{ $t('super_coupon_instructions') }}</h4>
					<p>{{ usageInstructionsText }}</p>
				</div>

				<div class="super-coupon-dialog__footer">
					<button class="super-coupon-dialog__confirm" type="button" @click="closeDetails">{{ $t('super_coupon_confirm') }}</button>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import { openThirdPartyPayment } from '../../utils/payment.js'

const CAROUSEL_INTERVAL = 5000

export default {
	name: 'LimitedCouponCard',
	props: {
		gameCode: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			activities: [],
			currentActivityIndex: 0,
			carouselDirection: 'next',
			carouselTimer: null,
			carouselHovered: false,
			carouselFocused: false,
			carouselTouching: false,
			touchStartX: null,
			couponRequestId: 0,
			componentDestroyed: false,
			paymentOpening: false,
			showDetails: false,
			countdownNow: Date.now(),
			countdownStartedAt: Date.now(),
			countdownTimer: null
		}
	},
	computed: {
		activity() {
			return this.activities[this.currentActivityIndex] || null
		},
		activityKey() {
			if (!this.activity) return 'empty'
			return this.activity.activityId || `${this.activity.productId || 'activity'}-${this.currentActivityIndex}`
		},
		hasMultipleActivities() {
			return this.activities.length > 1
		},
		carouselTransitionName() {
			return this.carouselDirection === 'previous'
				? 'limited-coupon-slide-previous'
				: 'limited-coupon-slide-next'
		},
		currentGoods() {
			if (!this.activity) return {}
			const packagePrice = this.activity.packagePrice || {}
			return {
				activityId: this.activity.activityId,
				productId: this.activity.productId,
				tips: this.activity.packageName || this.$t('super_coupon_package_name'),
				describe: this.activity.usageInstructions || this.activity.promotionalCopy || '',
				amount: packagePrice.amount,
				currency: packagePrice.currency,
				icon: this.activity.icon,
				purchaseChannel: this.activity.purchaseChannel,
				buttonText: this.formatMoney(packagePrice),
				points: 0,
				isSuperCoupon: true
			}
		},
		shouldShow() {
			return this.isActivityActive(this.activity, this.countdownNow)
		},
		packageTitle() {
			return (this.activity && this.activity.packageName) || this.currentGoods.tips || this.$t('super_coupon_package_name')
		},
		promotionalCopyText() {
			if (this.activity && this.activity.promotionalCopy) return this.activity.promotionalCopy
			return this.$t('super_coupon_get_coupons', { count: this.benefits.length })
		},
		currentPrice() {
			return this.activity && this.activity.packagePrice ? this.formatMoney(this.activity.packagePrice) : ''
		},
		realValueText() {
			return this.activity && this.activity.realDiscountValue
				? this.formatMoney(this.activity.realDiscountValue)
				: ''
		},
		rewardText() {
			if (!this.activity || !this.activity.packagePrice || !this.activity.realDiscountValue) return ''
			const price = Number(this.activity.packagePrice.amount)
			const value = Number(this.activity.realDiscountValue.amount)
			if (!Number.isFinite(price) || !Number.isFinite(value) || price <= 0 || value <= price) return ''
			const multiple = value / price
			return this.$t('super_coupon_reward_multiple', {
				multiple: multiple >= 10 ? Math.round(multiple) : multiple.toFixed(1)
			})
		},
		purchaseRuleText() {
			if (!this.activity) return ''
			const rules = []
			if (Number(this.activity.purchaseLimit) > 0) {
				rules.push(this.$t('super_coupon_purchase_limit', { count: this.activity.purchaseLimit }))
			}
			if (Number(this.activity.purchaseIntervalDays) > 0) {
				rules.push(this.$t('super_coupon_purchase_interval', { days: this.activity.purchaseIntervalDays }))
			}
			return rules.join(this.$t('super_coupon_rule_separator'))
		},
		hasPurchased() {
			return !!this.activity && this.activity.purchased === true
		},
		usageInstructionsText() {
			return this.activity && this.activity.usageInstructions
				? String(this.activity.usageInstructions)
				: ''
		},
		benefits() {
			const coupons = this.activity && Array.isArray(this.activity.coupons) ? this.activity.coupons : []
			return coupons.map(coupon => {
				const instance = this.getCouponInstance(coupon.instances, 0)
				const display = this.getInstanceDisplay(instance)
				return {
					threshold: this.formatThreshold(coupon.threshold),
					discount: this.formatMoney(coupon.discount),
					validity: this.formatValidity(coupon.validity),
					...display
				}
			})
		},
		detailCoupons() {
			const coupons = this.activity && Array.isArray(this.activity.coupons) ? this.activity.coupons : []
			return coupons.reduce((items, coupon, couponIndex) => {
				const instances = Array.isArray(coupon.instances) ? coupon.instances : []
				const baseCoupon = {
					id: coupon.couponCode || `coupon-${couponIndex}`,
					threshold: this.$t('super_coupon_threshold', { amount: this.formatThreshold(coupon.threshold) }),
					discount: this.$t('super_coupon_discount', { amount: this.formatMoney(coupon.discount) }),
					state: 'default',
					stateText: ''
				}
				if (!this.hasPurchased || !instances.length) {
					items.push(baseCoupon)
					return items
				}
				instances.forEach((instance, instanceIndex) => {
					if (!instance || typeof instance !== 'object') return
					const display = this.getInstanceDisplay(instance)
					items.push({
						id: instance.couponId || `${coupon.couponCode || couponIndex}-${instanceIndex}`,
						threshold: baseCoupon.threshold,
						discount: baseCoupon.discount,
						state: display.state,
						stateText: display.state === 'active'
							? this.$t('super_coupon_remaining', { time: display.remainingTime })
							: display.statusText || this.$t('super_coupon_unknown_status')
					})
				})
				return items
			}, [])
		},
		formattedEndTime() {
			const endTime = this.activity && this.activity.endTime
			if (!endTime) return ''
			const date = new Date(this.toTimestamp(endTime))
			if (Number.isNaN(date.getTime())) return ''
			const pad = value => String(value).padStart(2, '0')
			return this.$t('super_coupon_date_format', {
				year: date.getFullYear(),
				month: pad(date.getMonth() + 1),
				day: pad(date.getDate()),
				time: `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
			})
		}
	},
	watch: {
		gameCode() {
			this.loadSuperCoupons()
		}
	},
	mounted() {
		this.loadSuperCoupons()
		this.countdownTimer = window.setInterval(() => {
			this.countdownNow = Date.now()
			this.removeInactiveActivities()
		}, 1000)
		window.addEventListener('keydown', this.handleKeydown)
	},
	beforeDestroy() {
		this.componentDestroyed = true
		this.couponRequestId += 1
		if (this.countdownTimer) window.clearInterval(this.countdownTimer)
		this.stopCarousel()
		window.removeEventListener('keydown', this.handleKeydown)
	},
	methods: {
		openDetails() {
			this.stopCarousel()
			this.showDetails = true
			this.$nextTick(() => {
				if (this.$refs.detailsPanel) {
					this.$refs.detailsPanel.scrollLeft = 0
					this.$refs.detailsPanel.scrollTop = 0
				}
				if (this.$refs.detailsClose) this.$refs.detailsClose.focus()
			})
		},
		closeDetails() {
			this.showDetails = false
			this.$nextTick(() => {
				if (this.$refs.detailsTrigger) this.$refs.detailsTrigger.focus()
				this.startCarousel()
			})
		},
		handleKeydown(event) {
			if (event.key === 'Escape' && this.showDetails) this.closeDetails()
		},
		openPayment() {
			if (this.paymentOpening || this.hasPurchased) return
			this.stopCarousel()

			const roleInfo = this.$cache.getRoleInfo() || {}
			if (!this.$session.hasLogin() || !roleInfo.roleId || !roleInfo.serverCode) {
				this.$emit('buy', this.currentGoods)
				return
			}

			this.paymentOpening = true
			this.$setLoading('superCouponBuyButton', true)
			try {
				const purchaseChannel = this.activity.purchaseChannel || ''
				const separatorIndex = purchaseChannel.indexOf(':')
				const channel = separatorIndex === -1
					? { channel: purchaseChannel, paymentType: purchaseChannel }
					: {
						channel: purchaseChannel.slice(0, separatorIndex),
						paymentType: purchaseChannel.slice(separatorIndex + 1)
					}

				openThirdPartyPayment({
					gameData: this.$store.state.gameData,
					roleInfo,
					goods: this.currentGoods,
					channel
				})
			} catch (error) {
				this.paymentOpening = false
				this.$setLoading('superCouponBuyButton', false)
				this.startCarousel()
				console.error('[SuperCoupon] Failed to open payment', error)
			}
		},
		async loadSuperCoupons() {
			const requestId = ++this.couponRequestId
			this.stopCarousel()
			this.showDetails = false
			this.activities = []
			this.currentActivityIndex = 0
			const gameCode = this.gameCode || this.$cache.getGameCode()
			if (!gameCode) return

			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/coupon/getSuperCoupons',
					params: { gameCode, userId: this.$session.getUserId() },
					silentError: true
				})
				if (this.componentDestroyed || requestId !== this.couponRequestId) return
				if (!response || response.code !== 1000 || response.successful === false) return

				this.countdownStartedAt = Date.now()
				this.countdownNow = this.countdownStartedAt
				this.activities = this.getActiveActivities(response.data, gameCode)
				this.$nextTick(() => {
					if (!this.componentDestroyed && requestId === this.couponRequestId) this.startCarousel()
				})
			} catch (error) {
				if (this.componentDestroyed || requestId !== this.couponRequestId) return
				this.activities = []
			}
		},
		getActiveActivities(data, gameCode) {
			const activities = Array.isArray(data) ? data : [data]
			return activities.filter(activity => {
				if (!activity || typeof activity !== 'object') return false
				if (activity.gameCode && activity.gameCode !== gameCode) return false
				return this.isActivityActive(activity)
			})
		},
		removeInactiveActivities() {
			if (!this.activities.length) return

			const currentActivity = this.activity
			const activeActivities = this.activities.filter(activity => {
				return this.isActivityActive(activity, this.countdownNow)
			})
			if (activeActivities.length === this.activities.length) return

			this.activities = activeActivities
			const retainedIndex = currentActivity ? activeActivities.indexOf(currentActivity) : -1
			this.currentActivityIndex = retainedIndex >= 0
				? retainedIndex
				: Math.min(this.currentActivityIndex, Math.max(activeActivities.length - 1, 0))
			this.startCarousel()
		},
		isActivityActive(activity, now = Date.now()) {
			if (!activity || activity.isActive === false) return false

			const startTime = this.toTimestamp(activity.startTime)
			const endTime = this.toTimestamp(activity.endTime)
			return (!Number.isFinite(startTime) || startTime <= now) &&
				(!Number.isFinite(endTime) || endTime > now)
		},
		startCarousel() {
			this.stopCarousel()
			if (this.componentDestroyed || !this.hasMultipleActivities || this.showDetails || this.paymentOpening) return
			if (this.carouselHovered || this.carouselFocused || this.carouselTouching) return
			if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

			this.carouselTimer = window.setInterval(() => {
				this.changeActivity(this.currentActivityIndex + 1, 'next', false)
			}, CAROUSEL_INTERVAL)
		},
		stopCarousel() {
			if (this.carouselTimer) window.clearInterval(this.carouselTimer)
			this.carouselTimer = null
		},
		changeActivity(index, direction, restartTimer = true) {
			const activityCount = this.activities.length
			if (!activityCount) return

			const nextIndex = (index + activityCount) % activityCount
			if (nextIndex !== this.currentActivityIndex) {
				this.carouselDirection = direction
				this.currentActivityIndex = nextIndex
				this.showDetails = false
			}
			if (restartTimer) this.startCarousel()
		},
		showPreviousActivity() {
			this.changeActivity(this.currentActivityIndex - 1, 'previous')
		},
		showNextActivity() {
			this.changeActivity(this.currentActivityIndex + 1, 'next')
		},
		selectActivity(index) {
			if (index === this.currentActivityIndex) {
				this.startCarousel()
				return
			}
			this.changeActivity(index, index < this.currentActivityIndex ? 'previous' : 'next')
		},
		handleCarouselMouseEnter() {
			this.carouselHovered = true
			this.stopCarousel()
		},
		handleCarouselMouseLeave() {
			this.carouselHovered = false
			this.startCarousel()
		},
		handleCarouselFocusIn() {
			this.carouselFocused = true
			this.stopCarousel()
		},
		handleCarouselFocusOut(event) {
			const nextTarget = event.relatedTarget
			if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
				this.carouselFocused = false
				this.startCarousel()
			}
		},
		handleTouchStart(event) {
			this.carouselTouching = true
			this.stopCarousel()
			const touch = event.touches && event.touches[0]
			this.touchStartX = touch ? touch.clientX : null
		},
		handleTouchEnd(event) {
			const touch = event.changedTouches && event.changedTouches[0]
			const endX = touch ? touch.clientX : null
			if (this.touchStartX == null || endX == null) {
				this.touchStartX = null
				this.carouselTouching = false
				this.startCarousel()
				return
			}

			const distance = endX - this.touchStartX
			this.touchStartX = null
			this.carouselTouching = false
			if (Math.abs(distance) < 40) {
				this.startCarousel()
				return
			}
			if (distance > 0) this.showPreviousActivity()
			else this.showNextActivity()
		},
		handleTouchCancel() {
			this.touchStartX = null
			this.carouselTouching = false
			this.startCarousel()
		},
		getCouponInstance(instances, sourceType) {
			if (!Array.isArray(instances)) return null

			return instances.reduce((selected, instance) => {
				if (!instance || typeof instance !== 'object') return selected
				if (sourceType != null && Number(instance.sourceType) !== Number(sourceType)) return selected
				if (!selected) return instance

				const statusPriority = { 1: 3, 2: 2, 3: 1 }
				const selectedPriority = statusPriority[Number(selected.status)] || 0
				const currentPriority = statusPriority[Number(instance.status)] || 0
				return currentPriority > selectedPriority ? instance : selected
			}, null)
		},
		getInstanceDisplay(instance) {
			const instanceStatus = instance ? Number(instance.status) : null
			const remainingSeconds = instanceStatus === 1
				? this.getRemainingValiditySeconds(instance.remainingValiditySeconds)
				: null
			const status = instanceStatus === 1 && remainingSeconds === 0 ? 3 : instanceStatus
			const statusText = status === 2
				? this.$t('super_coupon_used')
				: status === 3 ? this.$t('super_coupon_expired') : ''
			const state = status === 1 && remainingSeconds != null
				? 'active'
				: status === 2 ? 'used' : status === 3 ? 'expired' : 'default'

			return {
				remainingTime: state === 'active' ? this.formatDuration(remainingSeconds) : '',
				statusText,
				state
			}
		},
		getRemainingValiditySeconds(remainingValiditySeconds) {
			const initialSeconds = Number(remainingValiditySeconds)
			if (!Number.isFinite(initialSeconds)) return null

			const elapsedSeconds = Math.floor((this.countdownNow - this.countdownStartedAt) / 1000)
			return Math.max(0, Math.floor(initialSeconds) - elapsedSeconds)
		},
		formatDuration(totalSeconds) {
			if (!Number.isFinite(totalSeconds)) return ''

			const hours = Math.floor(totalSeconds / 3600)
			const minutes = Math.floor(totalSeconds % 3600 / 60)
			const seconds = totalSeconds % 60
			const pad = value => String(value).padStart(2, '0')
			return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
		},
		toTimestamp(value) {
			if (value instanceof Date) return value.getTime()
			if (typeof value === 'number') return value < 1000000000000 ? value * 1000 : value
			if (typeof value !== 'string' || !value.trim()) return NaN
			if (/^\d+$/.test(value.trim())) {
				const numericValue = Number(value)
				return numericValue < 1000000000000 ? numericValue * 1000 : numericValue
			}
			return new Date(value.trim().replace(' ', 'T')).getTime()
		},
		formatThreshold(money) {
			return this.formatMoney(money, this.$i18n.locale !== 'ko_KR')
		},
		formatMoney(money, includeCurrency = true) {
			if (!money || money.amount == null) return ''
			const numericAmount = Number(money.amount)
			const amount = Number.isFinite(numericAmount)
				? numericAmount.toLocaleString('en-US', { maximumFractionDigits: 2 })
				: money.amount
			return `${includeCurrency ? money.currency || '' : ''} ${amount}`.trim()
		},
		formatValidity(validity) {
			if (!validity || validity.value == null) return ''
			const unit = String(validity.unit).toLowerCase()
			const unitKey = ['day', 'days', 'hour', 'hours', 'minute', 'minutes'].includes(unit)
				? `super_coupon_unit_${unit}`
				: null
			return this.$t('super_coupon_valid_for', {
				value: validity.value,
				unit: unitKey ? this.$t(unitKey) : validity.unit
			})
		}
	}
}
</script>

<style scoped>
.limited-coupon {
	box-sizing: border-box;
	width: 100%;
	min-width: 0;
	padding: 14px 20px 12px;
	border: 1px solid #eadfd6;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 2px 12px rgba(26, 35, 48, .05);
}

.limited-coupon__header {
	position: static;
	top: auto;
	z-index: auto;
	display: flex;
	box-sizing: border-box;
	width: auto;
	height: auto;
	min-height: 38px;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 0 4px 8px;
	border-bottom: 1px solid #eadfd6;
	background: transparent;
	text-align: left;
}

.limited-coupon__header h2 {
	color: #171717;
	font-size: 20px;
	font-weight: 700;
	line-height: 28px;
}

.limited-coupon__header p {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 0;
	font-size: 12px;
	line-height: 20px;
	text-align: right;
}

.limited-coupon__header p span {
	color: #665d56;
}

.limited-coupon__header time {
	color: #f0522a;
	font-size: 14px;
}

.limited-coupon__slider {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 0;
	padding: 10px 26px 0;
}

.limited-coupon__product {
	position: relative;
	box-sizing: border-box;
	width: 100%;
	max-width: none;
	min-height: 0;
	padding: 32px 16px 0;
	border: 1px solid #f0d8c9;
	border-radius: 7px;
	background: #fff;
}

.limited-coupon__product--no-value .limited-coupon__summary { grid-column: 1 / -1; }

.limited-coupon__top,
.limited-coupon__bottom {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(200px, .36fr);
	align-items: center;
	column-gap: 22px;
}

.limited-coupon__top {
	min-height: 112px;
	border-bottom: 1px solid #ece5df;
	padding-bottom: 12px;
}

.limited-coupon__bottom { min-height: 72px; }

.limited-coupon__nav {
	position: absolute;
	top: 50%;
	z-index: 3;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	width: 38px;
	height: 38px;
	padding: 0;
	border: 1px solid #eadfd6;
	border-radius: 50%;
	background: #fff;
	color: #f0522a;
	font-size: 13px;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(68, 48, 34, .1);
	transform: translateY(-50%);
}

.limited-coupon__nav--previous {
	left: 0;
}

.limited-coupon__nav--next {
	right: 0;
}

.limited-coupon__nav:hover {
	border-color: #f2b294;
	background: #fff8f4;
}

.limited-coupon__nav:focus-visible,
.limited-coupon__dot:focus-visible {
	outline: 2px solid #f0522a;
	outline-offset: 2px;
}

.limited-coupon__pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	min-height: 10px;
	margin-top: 7px;
}

.limited-coupon__dot {
	box-sizing: border-box;
	width: 9px;
	height: 9px;
	padding: 0;
	border: 0;
	border-radius: 5px;
	background: #d9d5d1;
	cursor: pointer;
	transition: width .22s ease, background-color .22s ease;
}

.limited-coupon__dot--active {
	width: 24px;
	background: #f0522a;
}

.limited-coupon-slide-next-enter-active,
.limited-coupon-slide-next-leave-active,
.limited-coupon-slide-previous-enter-active,
.limited-coupon-slide-previous-leave-active {
	transition: opacity .25s ease, transform .25s ease;
}

.limited-coupon-slide-next-enter,
.limited-coupon-slide-previous-leave-to {
	opacity: 0;
	transform: translateX(22px);
}

.limited-coupon-slide-next-leave-to,
.limited-coupon-slide-previous-enter {
	opacity: 0;
	transform: translateX(-22px);
}

.limited-coupon__badge {
	position: absolute;
	top: 0;
	left: 0;
	padding: 5px 10px;
	border-radius: 6px 0 5px 0;
	background: #ef5a35;
	color: #fff;
	font-size: 12px;
	font-weight: 700;
	line-height: 18px;
}

.limited-coupon__info {
	position: absolute;
	top: 8px;
	right: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border: 1px solid #d8cec5;
	border-radius: 50%;
	background: #fff;
	color: #665d56;
	font-size: 13px;
	cursor: pointer;
	box-shadow: 0 2px 5px rgba(30, 35, 40, .08);
}

.limited-coupon__summary {
	display: grid;
	grid-template-columns: 100px minmax(0, 1fr);
	align-items: center;
	gap: 14px;
	min-width: 0;
	min-height: 100px;
	margin-top: 0;
}

.limited-coupon__image-wrap {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100px;
	height: 96px;
	overflow: hidden;
	background: transparent;
}

.limited-coupon__image-wrap img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.limited-coupon__copy h3 {
	color: #151515;
	font-size: 18px;
	font-weight: 700;
	line-height: 26px;
	overflow-wrap: anywhere;
}

.limited-coupon__copy p {
	margin-top: 2px;
	color: #262626;
	font-size: 12px;
	line-height: 18px;
}

.limited-coupon__copy strong {
	display: block;
	margin-top: 3px;
	color: #e95320;
	font-size: 25px;
	font-weight: 700;
	line-height: 34px;
	white-space: nowrap;
}

.limited-coupon__value {
	display: flex;
	align-items: center;
	justify-content: space-between;
	box-sizing: border-box;
	min-width: 0;
	min-height: 64px;
	align-self: center;
	gap: 8px;
	margin-top: 0;
	padding: 8px 12px;
	border: 1px solid #f0d8c9;
	border-radius: 6px;
	background: #fffaf7;
}

.limited-coupon__value span,
.limited-coupon__value small {
	display: block;
	color: #4f423b;
	font-size: 11px;
	line-height: 17px;
}

.limited-coupon__value strong {
	display: block;
	color: #c9431a;
	font-size: 22px;
	font-weight: 700;
	line-height: 30px;
}

.limited-coupon__benefits {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	margin-top: 0;
}

.limited-coupon__benefit-copy { min-width: 0; padding: 10px 0 8px; }

.limited-coupon__benefits li {
	position: relative;
	display: flex;
	align-items: center;
	min-width: 0;
	padding: 0 5px;
	flex-direction: column;
	border-right: 1px solid #ece5df;
	text-align: center;
}

.limited-coupon__benefits li:last-child {
	border-right: 0;
}

.limited-coupon__benefits span,
.limited-coupon__benefits strong,
.limited-coupon__benefits small {
	display: block;
	font-size: 10px;
	line-height: 16px;
	white-space: nowrap;
}

.limited-coupon__benefits strong {
	color: #e95320;
	font-weight: 600;
}

.limited-coupon__benefits small {
	color: #a34b2b;
}

.limited-coupon__benefit--used .limited-coupon__threshold,
.limited-coupon__benefit--used .limited-coupon__discount {
	color: #bdb7b2;
}

.limited-coupon__benefit--expired .limited-coupon__threshold,
.limited-coupon__benefit--expired .limited-coupon__discount,
.limited-coupon__benefit--expired small {
	color: #aaa5a1;
}

.limited-coupon__benefits .limited-coupon__status-stamp {
	position: absolute;
	top: 21px;
	left: 50%;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	min-width: 48px;
	height: 23px;
	padding: 0 5px;
	border: 2px solid #ef5b45;
	border-radius: 2px;
	background: #fff;
	color: #ef5b45;
	font-size: 13px;
	font-weight: 700;
	line-height: 19px;
	transform: translate(-50%, -50%) rotate(-7deg);
	white-space: nowrap;
}

.limited-coupon__benefit--expired .limited-coupon__status-stamp {
	border-color: #999591;
	color: #999591;
}

.limited-coupon__purchase-rule {
	margin-top: 4px;
	color: #7a7070;
	font-size: 11px;
	line-height: 17px;
	text-align: center;
}

.limited-coupon__buy {
	display: block;
	width: 100%;
	height: 42px;
	margin: 0;
	border: 0;
	border-radius: 22px;
	color: #fff;
	background: #f25522;
	font-size: 16px;
	font-weight: 700;
	cursor: pointer;
}

.limited-coupon__buy:hover:not(:disabled) { background: #d94818; }
.limited-coupon__buy:focus-visible { outline: 2px solid #f0522a; outline-offset: 2px; }

.limited-coupon__buy:disabled {
	cursor: not-allowed;
	opacity: .72;
}

.limited-coupon__buy--purchased:disabled {
	background: #dedbd7 !important;
	color: #6f6963;
	opacity: 1;
}

.super-coupon-dialog__mask {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 10010;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	padding: 20px;
	background: rgba(20, 20, 20, .58);
}

.super-coupon-dialog {
	display: flex;
	box-sizing: border-box;
	width: min(460px, calc(100vw - 32px));
	max-height: calc(100vh - 40px);
	padding: 24px 26px 22px;
	overflow: hidden;
	flex-direction: column;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 16px 42px rgba(0, 0, 0, .2);
}

.super-coupon-dialog__header {
	position: relative;
	display: flex;
	align-items: center;
	min-height: 42px;
	padding-right: 42px;
	flex: 0 0 auto;
	border-bottom: 1px solid #dcdfe3;
}

.super-coupon-dialog__header,
.super-coupon-dialog__list,
.super-coupon-dialog__empty,
.super-coupon-dialog__instructions,
.super-coupon-dialog__footer {
	box-sizing: border-box;
	width: 100%;
	min-width: 0;
}

.super-coupon-dialog__header h3 {
	color: #25282c;
	font-size: 22px;
	font-weight: 700;
	line-height: 30px;
}

.super-coupon-dialog__close {
	position: absolute;
	top: -12px;
	right: -14px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #f2f1ef;
	color: #77736f;
	font-size: 16px;
	cursor: pointer;
}

.super-coupon-dialog__close:focus-visible,
.super-coupon-dialog__confirm:focus-visible {
	outline: 2px solid #f0522a;
	outline-offset: 2px;
}

.super-coupon-dialog__list {
	min-height: 0;
	max-height: 300px;
	overflow-y: auto;
	flex: 0 1 auto;
}

.super-coupon-dialog__list li {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	align-items: center;
	gap: 14px;
	box-sizing: border-box;
	min-height: 58px;
	padding: 11px 3px;
	border-bottom: 1px solid #dcdfe3;
}

.super-coupon-dialog__list span,
.super-coupon-dialog__list strong,
.super-coupon-dialog__list small {
	min-width: 0;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0;
	white-space: nowrap;
}

.super-coupon-dialog__list span {
	color: black;
	text-align: left;
}

.super-coupon-dialog__list strong {
	color: #f0522a;
	font-size: 16px;
	font-weight: 500;
	text-align: left;
}

.super-coupon-dialog__list small {
	color: #757b82;
	font-size: 12px;
	text-align: right;
}

.super-coupon-dialog__item--used small {
	color: #e84f2a;
	font-weight: 600;
}

.super-coupon-dialog__item--expired span,
.super-coupon-dialog__item--expired strong,
.super-coupon-dialog__item--expired small {
	color: #aaa5a1;
}

.super-coupon-dialog__empty {
	padding: 34px 8px;
	border-bottom: 1px solid #dcdfe3;
	color: #8a8f95;
	font-size: 14px;
	line-height: 22px;
	text-align: center;
}

.super-coupon-dialog__instructions {
	margin-top: 16px;
	padding: 13px 16px;
	overflow-y: auto;
	flex: 0 1 auto;
	border-left: 3px solid #f6a16f;
	background: #fff8f2;
}

.super-coupon-dialog__instructions h4 {
	color: #303236;
	font-size: 13px;
	font-weight: 600;
	line-height: 20px;
}

.super-coupon-dialog__instructions p {
	margin-top: 4px;
	color: #666b70;
	font-size: 12px;
	line-height: 19px;
	white-space: pre-line;
	overflow-wrap: anywhere;
}

.super-coupon-dialog__footer {
	display: flex;
	width: auto;
	height: auto;
	justify-content: flex-end;
	padding-top: 18px;
	flex: 0 0 auto;
	background: transparent;
}

.super-coupon-dialog__confirm {
	width: 88px;
	height: 44px;
	padding: 0;
	border: 0;
	border-radius: 22px;
	background: #f25522;
	color: #fff;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

@media (max-width: 960px) {
	.limited-coupon { padding: 14px; }
	.limited-coupon__slider { padding: 10px 18px 0; }

	.limited-coupon__product {
		padding: 30px 14px 0;
	}

	.limited-coupon__top,
	.limited-coupon__bottom { grid-template-columns: minmax(0, 1fr); }
	.limited-coupon__top { min-height: 0; }
	.limited-coupon__bottom { min-height: 0; }

	.limited-coupon__nav {
		width: 30px;
		height: 30px;
		font-size: 11px;
	}

	.limited-coupon__summary {
		grid-template-columns: 86px minmax(0, 1fr);
	}

	.limited-coupon__image-wrap {
		width: 86px;
		height: 86px;
	}

	.limited-coupon__copy h3 {
		font-size: 18px;
	}

	.limited-coupon__copy strong {
		font-size: 23px;
	}

	.limited-coupon__value { margin-top: 10px; }
	.limited-coupon__value strong {
		font-size: 22px;
	}
	.limited-coupon__benefit-copy { padding: 12px 0 10px; }
	.limited-coupon__purchase-rule { margin-top: 8px; }
	.limited-coupon__buy { margin: 0 0 12px; }
}

@media (max-width: 767px) {
	.limited-coupon {
		padding: 14px 12px;
	}

	.limited-coupon__header { align-items: flex-start; flex-wrap: wrap; gap: 2px 12px; }
	.limited-coupon__header h2 { font-size: 18px; }
	.limited-coupon__header p { justify-content: flex-start; text-align: left; }
	.limited-coupon__slider { padding: 10px 20px 0; }
	.limited-coupon__product {
		width: 100%;
		padding: 30px 12px 0;
	}
	.limited-coupon__summary { grid-template-columns: 76px minmax(0, 1fr); gap: 10px; }
	.limited-coupon__image-wrap { width: 76px; height: 76px; }
	.limited-coupon__copy h3 { font-size: 16px; line-height: 22px; }
	.limited-coupon__copy strong { font-size: 21px; line-height: 28px; }
	.limited-coupon__value strong { font-size: 20px; }
	.limited-coupon__benefits span,
	.limited-coupon__benefits strong,
	.limited-coupon__benefits small { white-space: normal; overflow-wrap: anywhere; }

	.limited-coupon__nav {
		width: 30px;
		height: 30px;
	}

	.limited-coupon__nav--previous {
		left: 0;
	}

	.limited-coupon__nav--next {
		right: 0;
	}

	.super-coupon-dialog__mask {
		padding: 16px;
	}

	.super-coupon-dialog {
		width: calc(100vw - 32px);
		padding: 22px 18px 18px;
	}

	.super-coupon-dialog__list li {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 6px;
	}

	.super-coupon-dialog__list small {
		grid-column: auto;
		text-align: right;
	}

}

@media (prefers-reduced-motion: reduce) {
	.limited-coupon__dot,
	.limited-coupon-slide-next-enter-active,
	.limited-coupon-slide-next-leave-active,
	.limited-coupon-slide-previous-enter-active,
	.limited-coupon-slide-previous-leave-active {
		transition: none;
	}
}
</style>
